/**
 * The single table of paywalled / AI / BYOK route prefixes.
 *
 * Three consumers used to keep private copies of these maps: the router's
 * `beforeLoad` guard in `frontend/src/main.tsx`, the nav filter in
 * `SystemShell.tsx`, and the paywall sweep test. Three copies of one table is
 * how they drift — and a drifted table produces the worst possible outcome: a
 * nav item that routes to a redirect, or a page that renders with every panel
 * blank.
 *
 * Money and AI are deliberately **separate** lists, not one list with a flag.
 * BYOK unlocks AI surfaces (the user pays their own provider); it must never
 * unlock a money surface, because a free deployment has nothing to sell. Adding
 * a money prefix to `AI_FEATURE_ROUTE_PREFIXES` would hand a wallet to a
 * deployment that has no wallet.
 *
 * Mirrors the backend prefix lists in `apps/payment/middleware.py`. See
 * `.claude/rules/feature-flag-paywall.md`.
 *
 * @module _shared_frontend/lib/paywall/routePrefixes
 */

/** Purchase surfaces. Gated by `paymentsEnabled` **only** — never by BYOK. */
export const MONEY_ROUTE_PREFIXES = [
  "/pricing",
  "/checkout",
  "/wallet",
] as const;

/** Retired subscription surfaces. Always hidden, on every deployment. */
export const REMOVED_SUBSCRIPTION_ROUTE_PREFIXES = [
  "/billing",
  "/plans",
  "/subscriptions",
] as const;

/**
 * AI surfaces → the `ai_features_enabled` key that decides whether the feature
 * *exists* on this deployment. Whether the current user can *use* it is a
 * separate, per-user question answered by `GET /api/v1/ai-keys/status/`.
 */
export const AI_FEATURE_ROUTE_PREFIXES: Readonly<Record<string, string>> = {
  "/ai-coaching": "ai_coaching",
  "/health-assistant": "health_assistant",
  "/meal-photo": "meal_photo",
  "/symptom-photo": "symptom_photo",
  "/messages": "community_chat",
  "/diet-protocols/meal-plans": "meal_planner",
};

/**
 * Surfaces that exist only because BYOK does. The key manager is pointless on
 * a deployment where users cannot bring a key, so it follows `byokEnabled`
 * rather than `paymentsEnabled`.
 */
export const BYOK_ROUTE_PREFIXES = ["/settings/ai-keys"] as const;

/** Prefix match on a path boundary, so "/wallet" never matches "/wallets". */
export function pathStartsWithPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function matchesAny(pathname: string, prefixes: readonly string[]): boolean {
  return prefixes.some((prefix) => pathStartsWithPrefix(pathname, prefix));
}

export function isMoneyPath(pathname: string): boolean {
  return matchesAny(pathname, MONEY_ROUTE_PREFIXES);
}

export function isRemovedSubscriptionPath(pathname: string): boolean {
  return matchesAny(pathname, REMOVED_SUBSCRIPTION_ROUTE_PREFIXES);
}

export function isByokPath(pathname: string): boolean {
  return matchesAny(pathname, BYOK_ROUTE_PREFIXES);
}

/** The AI feature key a path is gated by, or `undefined` if it is not an AI route. */
export function matchAIFeatureKey(pathname: string): string | undefined {
  return Object.entries(AI_FEATURE_ROUTE_PREFIXES).find(([prefix]) =>
    pathStartsWithPrefix(pathname, prefix),
  )?.[1];
}

/** The key manager page. Kept here so every deep link builds it one way. */
export const AI_KEYS_PATH = "/settings/ai-keys";

/**
 * Where the key manager should send a user back to once a key for `feature` is
 * saved. One table, so "Back to Voice Log" and the gate that links out agree.
 * `nameKey` is a `byok` namespace key holding the page's display name.
 */
export const AI_FEATURE_RETURN_TARGETS: Readonly<
  Record<string, { path: string; nameKey: string }>
> = {
  health_assistant_voice: {
    path: "/health-assistant/voice-log",
    nameKey: "deepLink.pages.health_assistant_voice",
  },
};

/**
 * Deep link into the key manager: `?provider=<slug>&feature=<key>#<slug>`.
 * The page scrolls to and highlights that provider's card, and offers a way
 * back to the feature after saving. Returns a path *without* language prefix.
 */
export function aiKeysDeepLinkPath(provider: string, feature?: string): string {
  const params = new URLSearchParams({ provider });
  if (feature) params.set("feature", feature);
  return `${AI_KEYS_PATH}?${params.toString()}#${provider}`;
}
